import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthContext } from '@/App';
import { Clock, Users, Calendar, FileText, Play, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { devError } from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const { user, loading, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [allowDemo, setAllowDemo] = useState(false);  // SEGURO POR DEFAULT: nunca mostrar demo

  useEffect(() => {
    // Carregar configuração pública (saber se demo-login está ativo neste ambiente)
    fetch(`${API}/settings/public`)
      .then((r) => (r.ok ? r.json() : { allow_demo_login: false }))
      .then((d) => setAllowDemo(d.allow_demo_login === true))  // só mostra se for explicitamente true
      .catch(() => setAllowDemo(false));
  }, []);

  // Recover password (emergency) — uses deletion password
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recEmail, setRecEmail] = useState('');
  const [recNewPass, setRecNewPass] = useState('');
  const [recDelPass, setRecDelPass] = useState('');
  const [recLoading, setRecLoading] = useState(false);

  const handleRecover = async () => {
    if (recLoading) return;
    if (!recEmail.trim() || !recNewPass || !recDelPass) {
      toast.error('Preencha todos os campos');
      return;
    }
    if (recNewPass.length < 8) {
      toast.error('Nova senha deve ter pelo menos 8 caracteres');
      return;
    }
    setRecLoading(true);
    try {
      const res = await fetch(`${API}/auth/recover-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: recEmail.trim().toLowerCase(),
          new_password: recNewPass,
          deletion_password: recDelPass,
        }),
      });
      const raw = await res.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = { detail: raw }; }
      if (!res.ok) throw new Error(data.detail || `Erro ${res.status}`);
      toast.success(data.message || 'Senha redefinida');
      setRecoverOpen(false);
      setLoginEmail(recEmail.trim().toLowerCase());
      setLoginPassword(recNewPass);
      setRecEmail(''); setRecNewPass(''); setRecDelPass('');
    } catch (err) {
      toast.error(err.message || 'Erro ao recuperar senha');
    } finally {
      setRecLoading(false);
    }
  };

  useEffect(() => {
    // Carregar configuração pública (saber se demo-login está ativo neste ambiente)
    fetch(`${API}/settings/public`)
      .then((r) => (r.ok ? r.json() : { allow_demo_login: true }))
      .then((d) => setAllowDemo(!!d.allow_demo_login))
      .catch(() => setAllowDemo(true));
  }, []);

  // Login robusto: tenta fetch e faz fallback para XMLHttpRequest se body vier "disturbed"
  // (problema causado por extensões de navegador ou service workers a intercetar a resposta)
  const doLoginRequest = (email, password) => new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.withCredentials = true;
      xhr.timeout = 15000;
      xhr.onload = () => {
        let data = {};
        try { data = xhr.responseText ? JSON.parse(xhr.responseText) : {}; }
        catch { data = { detail: xhr.responseText || 'Resposta inválida' }; }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data);
        else reject(new Error(data.detail || `Erro ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Erro de rede ao contactar o servidor'));
      xhr.ontimeout = () => reject(new Error('Tempo limite esgotado'));
      xhr.send(JSON.stringify({ email, password }));
    } catch (err) {
      reject(err);
    }
  });

  const handlePasswordLogin = async (e) => {
    e?.preventDefault?.();
    if (loginLoading) return;
    if (!loginEmail.trim() || !loginPassword) {
      toast.error('Preencha email e senha');
      return;
    }
    setLoginLoading(true);
    try {
      const data = await doLoginRequest(loginEmail.trim().toLowerCase(), loginPassword);
      setUser(data);
      toast.success('Sessão iniciada');
      if (data.must_change_password) {
        navigate('/admin/settings?force=change-password', { replace: true });
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      devError('login', err);
      toast.error(err.message || 'Erro ao iniciar sessão');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleDemoLogin = async (role = 'admin') => {
    if (demoLoading) return;
    setDemoLoading(true);
    try {
      // First seed data if needed
      await fetch(`${API}/seed`, { method: 'POST', credentials: 'include' });

      const res = await fetch(`${API}/auth/demo-login?role=${role}`, {
        method: 'POST',
        credentials: 'include'
      });
      const raw = await res.text();
      let userData = {};
      try { userData = raw ? JSON.parse(raw) : {}; } catch { userData = {}; }
      if (!res.ok) throw new Error(userData.detail || `Erro ${res.status}`);

      setUser(userData);
      navigate('/admin', { replace: true, state: { user: userData } });
    } catch (err) { devError("op", err);
      toast.error(err.message || 'Erro ao iniciar modo demo');
    } finally {
      setDemoLoading(false);
    }
  };

  React.useEffect(() => {
    if (user && !loading) {
      navigate('/admin');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-8 h-8 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="border-b border-[#E5E7EB] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#111111] flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-black tracking-tight" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              TimeTrack Bio
            </span>
          </div>
          <nav className="flex items-center gap-4">
            <a href="/portal" className="text-sm uppercase tracking-widest hover:text-[#0029FF] transition-colors" data-testid="nav-portal-link">
              Portal do Colaborador
            </a>
            <a href="/kiosk" className="text-sm uppercase tracking-widest hover:text-[#0029FF] transition-colors" data-testid="nav-kiosk-link">
              Quiosque
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1 max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#6B7280] mb-4">
              Sistema de Controlo de Ponto
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Satoshi, sans-serif' }}>
              Controlo de Ponto por PIN
            </h1>
            <p className="text-base lg:text-lg text-[#6B7280] mb-8 max-w-lg">
              Sistema completo para gestão de ponto com identificação por PIN e foto-prova.
              Multi-estabelecimento, escalas drag &amp; drop, relatórios em Excel.
            </p>
            
            <div className="flex flex-col gap-4">
              {/* Login form — modo primário de entrada */}
              <form onSubmit={handlePasswordLogin} className="space-y-3 border-2 border-[#111111] p-5 bg-white max-w-sm" data-testid="login-form">
                <div className="flex items-center gap-2 mb-1">
                  <Lock className="w-4 h-4 text-[#111111]" />
                  <span className="text-xs font-bold uppercase tracking-widest text-[#111111]">Entrar com email e senha</span>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest">Email</Label>
                  <Input
                    type="email"
                    autoComplete="username"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="admin@pontodg.com"
                    className="h-10"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase tracking-widest">Senha</Label>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-10"
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full bg-[#111111] hover:bg-[#0029FF] text-white text-xs uppercase tracking-widest font-semibold h-10"
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : 'Entrar'}
                </Button>
                <button
                  type="button"
                  onClick={() => setRecoverOpen(true)}
                  className="w-full text-[10px] uppercase tracking-widest text-[#6B7280] hover:text-[#DC2626] mt-1"
                  data-testid="forgot-password-link"
                >
                  Esqueceu a senha?
                </button>
              </form>

              {/* Demo buttons — visíveis apenas em ambientes com demo habilitado */}
              {allowDemo && (
                <div className="flex flex-col gap-2">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">Entrar como demo</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid="demo-login-btn"
                      onClick={() => handleDemoLogin('admin')}
                      disabled={demoLoading}
                      className="bg-[#0029FF] hover:bg-[#0022CC] text-white px-6 py-5 text-xs uppercase tracking-widest font-semibold"
                    >
                      {demoLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Administrador
                        </>
                      )}
                    </Button>
                    <Button
                      data-testid="demo-login-rh-btn"
                      onClick={() => handleDemoLogin('rh')}
                      disabled={demoLoading}
                      className="bg-[#059669] hover:bg-[#047857] text-white px-6 py-5 text-xs uppercase tracking-widest font-semibold"
                    >
                      RH / Financeiro
                    </Button>
                    <Button
                      data-testid="demo-login-gestor-btn"
                      onClick={() => handleDemoLogin('gestor')}
                      disabled={demoLoading}
                      className="bg-[#F59E0B] hover:bg-[#D97706] text-white px-6 py-5 text-xs uppercase tracking-widest font-semibold"
                    >
                      Gestor
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex flex-wrap gap-4">
              <Button 
                data-testid="kiosk-mode-btn"
                onClick={() => navigate('/kiosk')}
                variant="ghost"
                className="text-sm uppercase tracking-widest text-[#6B7280] hover:text-[#111111]"
              >
                <Clock className="w-4 h-4 mr-2" />
                Modo Quiosque
              </Button>
              <Button 
                data-testid="portal-mode-btn"
                onClick={() => navigate('/portal')}
                variant="ghost"
                className="text-sm uppercase tracking-widest text-[#6B7280] hover:text-[#111111]"
              >
                <Users className="w-4 h-4 mr-2" />
                Portal Colaborador
              </Button>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-2 gap-4">
            <FeatureCard 
              icon={<Clock className="w-6 h-6" />}
              title="Controlo por PIN"
              description="Picagem de ponto por PIN + foto com marca d'água em tablets"
            />
            <FeatureCard 
              icon={<Users className="w-6 h-6" />}
              title="Multi-Local"
              description="Gestão de 3+ estabelecimentos simultâneos"
            />
            <FeatureCard 
              icon={<Calendar className="w-6 h-6" />}
              title="Escalas Drag & Drop"
              description="Monte escalas semanais arrastando turnos"
            />
            <FeatureCard 
              icon={<FileText className="w-6 h-6" />}
              title="Relatórios Excel"
              description="Exporte relatórios por local, funcionário ou período"
            />
          </div>
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] px-6 py-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xs text-[#6B7280] uppercase tracking-widest">
            TimeTrack Bio © {new Date().getFullYear()}
          </p>
        </div>
      </footer>

      {/* Recover password dialog */}
      <Dialog open={recoverOpen} onOpenChange={setRecoverOpen}>
        <DialogContent className="sm:max-w-md" data-testid="recover-dialog">
          <DialogHeader>
            <DialogTitle className="text-base uppercase tracking-widest">Recuperar Senha</DialogTitle>
            <DialogDescription className="text-xs">
              Use a <strong>senha de exclusão</strong> (definida em Configurações; padrão: <code>admin123</code>) para redefinir a senha do admin ou outro utilizador administrativo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Email do utilizador</Label>
              <Input
                type="email"
                value={recEmail}
                onChange={(e) => setRecEmail(e.target.value)}
                placeholder="admin@pontodg.com"
                className="h-10"
                data-testid="rec-email"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Nova Senha (mín. 8 caracteres)</Label>
              <Input
                type="password"
                value={recNewPass}
                onChange={(e) => setRecNewPass(e.target.value)}
                autoComplete="new-password"
                className="h-10"
                data-testid="rec-new-pass"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Senha de Exclusão (master)</Label>
              <Input
                type="password"
                value={recDelPass}
                onChange={(e) => setRecDelPass(e.target.value)}
                placeholder="admin123 (padrão)"
                className="h-10"
                data-testid="rec-del-pass"
              />
            </div>
            <Button
              onClick={handleRecover}
              disabled={recLoading}
              data-testid="rec-submit"
              className="w-full bg-[#DC2626] hover:bg-[#991B1B] text-white uppercase tracking-widest text-xs font-semibold"
            >
              {recLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : 'Redefinir Senha'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="border border-[#E5E7EB] p-6 hover:border-[#111111] transition-colors">
      <div className="w-12 h-12 bg-[#F3F4F6] flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-bold mb-2" style={{ fontFamily: 'Satoshi, sans-serif' }}>{title}</h3>
      <p className="text-sm text-[#6B7280]">{description}</p>
    </div>
  );
}
